import { useState, useEffect } from 'react'
import { timeTrackingService } from '@/services/timeTracking'
import type { TimeEntry } from '@/services/timeTracking'

interface LogoProps {
  className?: string
  showStatus?: boolean
}

export default function Logo({ className = '', showStatus = true }: LogoProps) {
  const [runningEntry, setRunningEntry] = useState<TimeEntry | null>(null)

  // Initial check and event-based updates for logo animation (only if showStatus is true)
  useEffect(() => {
    if (!showStatus) return

    const checkRunningEntry = async () => {
      try {
        const response = await timeTrackingService.getRunningEntry()
        setRunningEntry(response.runningEntry)
      } catch (error) {
        console.error('Error checking running entry:', error)
      }
    }

    // Initial check only
    checkRunningEntry()

    // Listen for custom events from time tracking actions
    const handleTimeTrackingUpdate = () => {
      checkRunningEntry()
    }

    // Add event listeners for time tracking actions
    window.addEventListener('timeEntryStarted', handleTimeTrackingUpdate)
    window.addEventListener('timeEntryStopped', handleTimeTrackingUpdate)
    window.addEventListener('timeEntryUpdated', handleTimeTrackingUpdate)

    return () => {
      window.removeEventListener('timeEntryStarted', handleTimeTrackingUpdate)
      window.removeEventListener('timeEntryStopped', handleTimeTrackingUpdate)
      window.removeEventListener('timeEntryUpdated', handleTimeTrackingUpdate)
    }
  }, [showStatus])

  return (
    <h1 className={`text-xl font-semibold transition-all duration-700 ease-out transform hover:scale-105 flex items-center space-x-2 relative overflow-hidden ${
      showStatus && runningEntry 
        ? 'text-blue-600 bg-blue-50/80 backdrop-blur-sm shadow-sm' 
        : showStatus 
          ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 hover:backdrop-blur-sm'
          : 'text-gray-900'
    } px-3 py-2 rounded-md ${className}`}>
      {/* Stylish slide effect overlay - only show if status is enabled */}
      {showStatus && (
        <div className={`absolute inset-0 transition-all duration-700 ease-out rounded-md ${
          runningEntry 
            ? 'bg-blue-50/80 backdrop-blur-sm shadow-sm transform translate-x-0' 
            : 'bg-gray-50/50 backdrop-blur-sm transform -translate-x-full'
        }`} />
      )}
      
      {/* Logo content with relative positioning */}
      <div className="relative z-10 flex items-center space-x-2">
        <svg 
          className={`w-6 h-6 transition-all duration-700 ease-out ${
            showStatus && runningEntry ? 'text-blue-600' : showStatus ? 'text-gray-500' : 'text-gray-900'
          }`}
          fill="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
          <path 
            className="transition-transform duration-1000 ease-out origin-center"
            style={{ transformOrigin: '12px 12px' }}
            d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"
          />
        </svg>
        <span className={`transition-all duration-700 ease-out ${
          showStatus && runningEntry ? 'text-blue-600' : showStatus ? 'text-gray-500' : 'text-gray-900'
        }`}>Promptime</span>
      </div>
    </h1>
  )
} 